// The portal theme ("flavor") — the phosphor-terminal palette is swappable
// (ADR-0032). The choice lives on <html data-flavor> (seeded in index.html so
// there's no flash) and persists to localStorage. Only the chrome themes; the
// round-preview pixels stay byte-pinned to the firmware dither (ADR-0028).

import { useEffect, useState } from "react";

export const FLAVORS = ["miami", "pastel", "synthwave", "terminal"] as const;
export type Flavor = (typeof FLAVORS)[number];

export const FLAVOR_LABELS: Record<Flavor, string> = {
  miami: "Miami sunset",
  pastel: "Pastel dream",
  synthwave: "Synthwave noir",
  terminal: "Terminal green",
};

const KEY = "pixie.portal.flavor";
const DEFAULT: Flavor = "miami";

// Each flavor's --bg (styles.css html[data-flavor] blocks) — drives <meta name="theme-color">
// so the mobile browser chrome matches the app ground instead of framing it in white.
const FLAVOR_BG: Record<Flavor, string> = {
  miami: "#160d2e",
  pastel: "#1d1530",
  synthwave: "#0c0718",
  terminal: "#050a06",
};

function isFlavor(v: string | null): v is Flavor {
  return v !== null && (FLAVORS as readonly string[]).includes(v);
}

/** Read the persisted flavor (falling back to the <html data-flavor> seed, then Miami). */
function initialFlavor(): Flavor {
  try {
    const saved = localStorage.getItem(KEY);
    if (isFlavor(saved)) return saved;
  } catch {
    /* localStorage can throw in private mode — fall through */
  }
  const seeded = typeof document !== "undefined" ? document.documentElement.dataset.flavor ?? null : null;
  return isFlavor(seeded) ? seeded : DEFAULT;
}

/** The current portal flavor + a setter that mirrors it to <html> and localStorage. */
export function useTheme(): readonly [Flavor, (f: Flavor) => void] {
  const [flavor, setFlavor] = useState<Flavor>(initialFlavor);

  useEffect(() => {
    document.documentElement.dataset.flavor = flavor;
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute("content", FLAVOR_BG[flavor]);
    try {
      localStorage.setItem(KEY, flavor);
    } catch {
      /* private mode — the in-memory choice still applies for this session */
    }
  }, [flavor]);

  return [flavor, setFlavor] as const;
}
